// Състоянието на „Целия списък": филтър, група, превключватели, отметки и
// маркиран ред + клавишите на полето за филтър и на полетата в редовете.
// Модалът е с destroyOnHidden и hook-ът живее ВЪТРЕ в тялото му → всяко
// отваряне започва на чисто. Правилата (какво е видимо, какво може да се
// отметне, кой клавиш какво прави) са в catalogPickerModel.ts, въведеното в
// редовете — в useCatalogEntries; тук е само свързването им.
//
// С `entry` (въвеждане в реда): Enter/клик по ред го отмята И слага курсора в
// количеството; Enter в поле → следващият ред (отметнат — направо в полето
// му; иначе маркерът слиза на него и фокусът се връща във филтъра); Shift+Enter
// само отмята/маха отметката; клик по квадратчето — също.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ALL_GROUPS,
  catalogColumnsOf,
  catalogGroupsOf,
  catalogKeyAction,
  catalogViewOf,
  firstSelectableIndex,
  idsToAdd,
  indexCatalog,
  isSelectable,
  searchWordsOf,
  stepGroup,
  toggleAllVisible,
  togglePicked,
  visibleSelectionOf,
  type CatalogItem,
} from "./catalogPickerModel";
import {
  pickedSumOf,
  type CatalogEntryConfig,
  type CatalogEntryValues,
} from "./catalogEntryModel";
import { useCatalogEntries } from "./useCatalogEntries";
import { CATALOG_HEADER_HEIGHT, CATALOG_ROW_HEIGHT, catalogRowVariantOf } from "./catalogPickerLayout";
import { useCatalogWindow } from "./useCatalogWindow";

type Options = {
  items: CatalogItem[];
  addedSelectable: boolean;
  isNarrow: boolean;
  listId: string;
  entry?: CatalogEntryConfig;
  onAdd: (ids: number[], values?: CatalogEntryValues) => void;
  onClose: () => void;
  onCreate?: (query: string) => void;
};

export const useCatalogPicker = ({
  items,
  addedSelectable,
  isNarrow,
  listId,
  entry,
  onAdd,
  onClose,
  onCreate,
}: Options) => {
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<string>(ALL_GROUPS);
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [hideAdded, setHideAdded] = useState(false);
  const [belowMinimum, setBelowMinimum] = useState(false);
  const [fromSupplier, setFromSupplier] = useState(false);
  // Set пази реда на отмятане → лентата показва последно отметнатите първи
  const [picked, setPicked] = useState<Set<number>>(() => new Set());
  const inputRef = useRef<HTMLInputElement | null>(null);

  const index = useMemo(() => indexCatalog(items), [items]);
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const groups = useMemo(() => catalogGroupsOf(items), [items]);
  const columns = useMemo(() => catalogColumnsOf(items), [items]);
  const anyStockTone = useMemo(() => items.some((item) => item.stockTone != null), [items]);
  const anyAdded = useMemo(() => items.some((item) => item.added), [items]);
  const anyBelowMinimum = useMemo(() => items.some((item) => item.belowMinimum), [items]);
  const anyFromSupplier = useMemo(() => items.some((item) => item.fromSupplier), [items]);
  const entries = useCatalogEntries({ entry, listId, byId });

  const words = useMemo(() => searchWordsOf(query), [query]);
  const view = useMemo(
    () => catalogViewOf(index, { words, group, onlyInStock, hideAdded, belowMinimum, fromSupplier }),
    [index, words, group, onlyInStock, hideAdded, belowMinimum, fromSupplier],
  );
  const { visible } = view;

  const variant = catalogRowVariantOf(isNarrow, columns);
  const listWindow = useCatalogWindow(visible.length, CATALOG_ROW_HEIGHT[variant], CATALOG_HEADER_HEIGHT);
  const { revealRow, pageSize } = listWindow;

  // Маркираният ред. Нов филтър (текстът, не масивът — пресмятане на същия
  // филтър с нови наличности не мести маркера) → първият ред, който може да
  // се отметне; нагласява се още в рендера (без ефект и двойно рисуване).
  const filterKey = `${words.join(" ")}|${group}|${onlyInStock}|${hideAdded}|${belowMinimum}|${fromSupplier}`;
  const [activeState, setActiveState] = useState(() => ({
    key: filterKey,
    index: firstSelectableIndex(visible, addedSelectable),
  }));
  let activeIndex = activeState.index;
  if (activeState.key !== filterKey) {
    activeIndex = firstSelectableIndex(visible, addedSelectable);
    setActiveState({ key: filterKey, index: activeIndex });
  }
  activeIndex = Math.max(0, Math.min(activeIndex, visible.length - 1));
  const active = visible[activeIndex] as CatalogItem | undefined;
  // ... и скролът се връща горе
  useEffect(() => revealRow(0), [filterKey, revealRow]);

  const setActiveIndex = useCallback(
    (next: number) => setActiveState((current) => (current.index === next ? current : { ...current, index: next })),
    [],
  );
  const moveActive = (next: number) => {
    setActiveIndex(next);
    revealRow(next);
  };

  const selection = useMemo(
    () => visibleSelectionOf(visible, picked, addedSelectable),
    [visible, picked, addedSelectable],
  );
  // Лентата с избраните — последно отметнатите първи; изчезнал ред не се брои
  const pickedItems = useMemo(
    () =>
      Array.from(picked)
        .reverse()
        .map((id) => byId.get(id))
        .filter((item): item is CatalogItem => !!item),
    [picked, byId],
  );

  const toggleItem = useCallback(
    (item: CatalogItem) => {
      if (!isSelectable(item, addedSelectable)) return;
      setPicked((current) => togglePicked(current, item.id));
    },
    [addedSelectable],
  );
  const focusInput = () => inputRef.current?.focus();
  const unpick = (id: number) => {
    // полето, в което се пише, изчезва с отметката → фокусът във филтъра
    if (entries.isEditing(id)) focusInput();
    setPicked((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  };
  const clearPicked = () => setPicked(new Set());
  const toggleVisible = () => setPicked((current) => toggleAllVisible(current, visible, addedSelectable));

  // Отмята реда (ако не е) и слага курсора в количеството му
  const { focusField } = entries;
  const editItem = useCallback(
    (item: CatalogItem, itemIndex: number) => {
      if (!isSelectable(item, addedSelectable)) return;
      setPicked((current) => (current.has(item.id) ? current : togglePicked(current, item.id)));
      setActiveIndex(itemIndex);
      focusField(item.id, "qty");
    },
    [addedSelectable, setActiveIndex, focusField],
  );

  const add = async () => {
    const ids = idsToAdd(items, picked, active, addedSelectable);
    if (!ids.length) return;
    if (!entry) {
      onAdd(ids);
      return;
    }
    const values = await entries.resolveValues(ids);
    if (values) onAdd(ids, values);
    else focusInput();
  };
  const create = onCreate ? () => onCreate(query.trim()) : undefined;
  // Групата от мишката/релсата; refocus — връща фокуса във филтъра (десктоп)
  const chooseGroup = (value: string, refocus = true) => {
    setGroup(value);
    if (refocus) focusInput();
  };

  // Enter при празен списък: има ли съвпадения в други групи — показва всички
  // групи (пази от дубликат); иначе „+ Нов продукт „…"" (ако листът го поддържа)
  const enterOnEmpty =
    group !== ALL_GROUPS && view.total > 0 ? "allGroups" : create && words.length ? "create" : null;

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const action = catalogKeyAction(event, {
      activeIndex,
      rowCount: visible.length,
      pageSize,
      hasQuery: query.length > 0,
      enterOnEmpty,
      entryMode: !!entry,
    });
    if (!action) return;
    // Всеки обработен клавиш спира тук: Esc не стига до antd (затваряне) и
    // до общия пазач, Enter не стига до нищо друго
    event.preventDefault();
    event.stopPropagation();
    switch (action.type) {
      case "move":
        moveActive(action.index);
        break;
      case "group":
        setGroup((current) => stepGroup(groups, current, action.step));
        break;
      case "toggle":
        if (active) toggleItem(active);
        break;
      case "edit":
        if (active) editItem(active, activeIndex);
        break;
      case "add":
        void add();
        break;
      case "allGroups":
        setGroup(ALL_GROUPS);
        break;
      case "create":
        create?.();
        break;
      case "clearQuery":
        setQuery("");
        break;
      case "close":
        onClose();
        break;
    }
  };

  // Клавишите извън полето (релсата, лентата, бутоните): Ctrl/Cmd+Enter
  // добавя отвсякъде, Esc връща фокуса във филтъра (стъпка назад, не затваряне)
  const onBodyKeyDown = (event: React.KeyboardEvent) => {
    if (event.target === inputRef.current) return;
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      void add();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      focusInput();
    }
  };

  // Enter в поле на реда → следващият ред: отметнат — курсорът в неговото
  // количество; иначе маркерът слиза на него, а фокусът се връща във филтъра
  // (с маркиран текст — нова дума се пише отгоре, Enter отмята маркирания).
  // Ctrl/Cmd+Enter и Esc стигат до тялото (добавяне / обратно във филтъра).
  const onFieldKeyDown = (itemIndex: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
    const nextIndex = Math.min(itemIndex + 1, visible.length - 1);
    const next = visible[nextIndex];
    if (nextIndex !== itemIndex) moveActive(nextIndex);
    if (next && nextIndex !== itemIndex && picked.has(next.id) && isSelectable(next, addedSelectable)) {
      focusField(next.id, "qty");
      return;
    }
    focusInput();
    inputRef.current?.select();
  };
  // стабилна (memo на редовете)
  const onFieldChange = entries.setField;
  const pickedSum = entry?.kind === "qtyPrice" ? pickedSumOf(pickedItems, entries.entries) : null;

  return {
    inputRef,
    query,
    setQuery,
    words,
    group,
    chooseGroup,
    groups,
    columns,
    variant,
    listWindow,
    view,
    visible,
    activeIndex,
    setActiveIndex,
    active,
    picked,
    pickedItems,
    selection,
    toggleItem,
    toggleVisible,
    unpick,
    clearPicked,
    onlyInStock,
    setOnlyInStock,
    hideAdded,
    setHideAdded,
    belowMinimum,
    setBelowMinimum,
    fromSupplier,
    setFromSupplier,
    anyStockTone,
    anyAdded,
    anyBelowMinimum,
    anyFromSupplier,
    entry,
    entries: entries.entries,
    pickedSum,
    editItem,
    onFieldKeyDown,
    onFieldChange,
    add,
    create,
    enterOnEmpty,
    onInputKeyDown,
    onBodyKeyDown,
  };
};

export type CatalogPickerState = ReturnType<typeof useCatalogPicker>;
